import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', promptLabel: 'English', announcementLanguage: 'en-AU', announcementVoice: 'en-AU-Wavenet-B' },
  { value: 'vi', label: 'Vietnamese', promptLabel: 'Vietnamese', announcementLanguage: 'vi-VN', announcementVoice: 'vi-VN-Wavenet-A' },
  { value: 'ru', label: 'Russian', promptLabel: 'Russian', announcementLanguage: 'ru-RU', announcementVoice: 'ru-RU-Wavenet-A' },
  { value: 'ko', label: 'Korean', promptLabel: 'Korean', announcementLanguage: 'ko-KR', announcementVoice: 'ko-KR-Wavenet-A' },
  { value: 'es', label: 'Spanish', promptLabel: 'Spanish', announcementLanguage: 'es-ES', announcementVoice: 'es-ES-Wavenet-A' },
  { value: 'fr', label: 'French', promptLabel: 'French', announcementLanguage: 'fr-FR', announcementVoice: 'fr-FR-Wavenet-A' },
  { value: 'de', label: 'German', promptLabel: 'German', announcementLanguage: 'de-DE', announcementVoice: 'de-DE-Wavenet-A' },
  { value: 'ja', label: 'Japanese', promptLabel: 'Japanese', announcementLanguage: 'ja-JP', announcementVoice: 'ja-JP-Wavenet-A' },
  { value: 'zh', label: 'Chinese (Mandarin)', promptLabel: 'Simplified Chinese', announcementLanguage: 'cmn-CN', announcementVoice: 'cmn-CN-Wavenet-A' },
];

const DEFAULT_VOUCHER_TERMS = [
  'One voucher per person.',
  'One claim per phone or device.',
  'Redeem by opening the voucher on this page, saving or sharing it to the phone, and presenting it at the venue.',
  'Cannot be exchanged for cash or combined with other promotions unless stated otherwise.',
  'The venue may refuse altered, copied, or expired vouchers.',
].join(' ');

const normalizeId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const buildSiteOptionLabel = (site) => String(site?.name || '').trim() || `Site #${site?.id || ''}`;

const buildSourceCatalogLabel = (sourceCatalog) => {
  if (!sourceCatalog) return '';
  return [sourceCatalog.siteName, sourceCatalog.areaName, sourceCatalog.title].filter(Boolean).join(' • ');
};

export default function PromptForm({
  siteId,
  site = null,
  area = null,
  onGenerate,
  onLanguageSelected,
  onGenerateStart,
  onGenerateError,
  generationRequest = null,
  requestKey = 0,
  showHeader = true,
}) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [language, setLanguage] = useState('en');
  const [promoteOtherSites, setPromoteOtherSites] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [promotedSiteIds, setPromotedSiteIds] = useState([]);
  const [sharedProductsContext, setSharedProductsContext] = useState(null);
  const [includeVoucher, setIncludeVoucher] = useState(false);
  const [voucherOfferDescription, setVoucherOfferDescription] = useState('');
  const [voucherTerms, setVoucherTerms] = useState(DEFAULT_VOUCHER_TERMS);
  const lastHandledRequestKeyRef = useRef(0);

  const selectedLanguage = useMemo(
    () => LANGUAGE_OPTIONS.find((option) => option.value === language) || LANGUAGE_OPTIONS[0],
    [language]
  );
  const currentSiteId = normalizeId(siteId);
  const promotedSiteOptions = useMemo(
    () => (Array.isArray(availableSites) ? availableSites : []).filter((siteOption) => (
      String(siteOption?.id || '') !== String(currentSiteId || '')
    )),
    [availableSites, currentSiteId]
  );
  const selectedPromotedSites = useMemo(
    () => promotedSiteIds
      .map((promotedSiteId) => promotedSiteOptions.find((siteOption) => String(siteOption?.id || '') === String(promotedSiteId)) || null)
      .filter(Boolean),
    [promotedSiteIds, promotedSiteOptions]
  );
  const requiresPromotedSiteSelection = promoteOtherSites && promotedSiteOptions.length > 0 && selectedPromotedSites.length === 0;
  const requiresVoucherDescription = includeVoucher && !String(voucherOfferDescription || '').trim();

  useEffect(() => {
    if (typeof onLanguageSelected === 'function') {
      onLanguageSelected({
        language: selectedLanguage.announcementLanguage,
        voice: selectedLanguage.announcementVoice,
      });
    }
  }, [selectedLanguage, onLanguageSelected]);

  const handleLanguageChange = (value) => {
    setLanguage(value);
  };

  useEffect(() => {
    setPromotedSiteIds((prev) => prev.filter((promotedSiteId) => String(promotedSiteId) !== String(currentSiteId || '')));
  }, [currentSiteId]);

  useEffect(() => {
    if (!includeVoucher) return;
    setVoucherTerms((prev) => (String(prev || '').trim() ? prev : DEFAULT_VOUCHER_TERMS));
  }, [includeVoucher]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;

    const loadSites = async () => {
      setSitesLoading(true);
      setSitesError('');
      try {
        const res = await fetch('/api/sites', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(body || `Failed to load sites (${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setAvailableSites(Array.isArray(data.sites) ? data.sites : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load promoted site options', err);
        setAvailableSites([]);
        setSitesError(err.message || 'Failed to load sites.');
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    };

    loadSites();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const areaId = normalizeId(area?.id);
    if (!token || !areaId) {
      setSharedProductsContext(null);
      return undefined;
    }
    let cancelled = false;

    const loadSharedProductsContext = async () => {
      try {
        const res = await fetch(`/api/catalogs/products/area/${encodeURIComponent(areaId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setSharedProductsContext(null);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const catalog = data.catalog || data.menu || null;
        const contentMode = String(catalog?.contentMode || catalog?.content_mode || '').trim().toLowerCase();
        if (contentMode !== 'shared' || !catalog?.sourceCatalog) {
          setSharedProductsContext(null);
          return;
        }
        setSharedProductsContext({
          catalogId: normalizeId(catalog.sourceCatalogId || catalog.source_catalog_id),
          sourceCatalog: catalog.sourceCatalog,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load shared products context', err);
          setSharedProductsContext(null);
        }
      }
    };

    loadSharedProductsContext();
    return () => {
      cancelled = true;
    };
  }, [area?.id]);

  const togglePromotedSite = (candidateSiteId) => {
    const normalizedSiteId = normalizeId(candidateSiteId);
    if (!normalizedSiteId) return;
    setPromotedSiteIds((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return current.some((siteEntry) => String(siteEntry) === String(normalizedSiteId))
        ? current.filter((siteEntry) => String(siteEntry) !== String(normalizedSiteId))
        : [...current, normalizedSiteId];
    });
  };

  const runGeneration = useCallback(async ({ promptText, autoGenerateArtwork }) => {
    const finalPrompt = String(promptText || '').trim();
    if (!finalPrompt) return;
    if (requiresPromotedSiteSelection) {
      if (typeof window !== 'undefined') {
        window.alert('Choose at least one promoted site before generating this multi-site campaign.');
      }
      return;
    }
    if (requiresVoucherDescription) {
      if (typeof window !== 'undefined') {
        window.alert('Add a short description of the voucher offer before generating.');
      }
      return;
    }
    setLoading(true);
    setLoadingAction(autoGenerateArtwork ? 'copy_artwork' : 'copy_only');
    const payload = {
      prompt: finalPrompt,
      siteId,
      language: {
        code: selectedLanguage.value,
        label: selectedLanguage.label,
        promptLabel: selectedLanguage.promptLabel,
      },
      promotedSiteIds: promoteOtherSites ? promotedSiteIds : [],
      sourceCatalogId: sharedProductsContext?.catalogId || null,
      voucherRequest: includeVoucher
        ? {
            enabled: true,
            offerDescription: String(voucherOfferDescription || '').trim(),
            terms: String(voucherTerms || '').trim() || DEFAULT_VOUCHER_TERMS,
          }
        : null,
    };
    if (typeof onGenerateStart === 'function') {
      onGenerateStart({
        autoGenerateArtwork: Boolean(autoGenerateArtwork),
        prompt: finalPrompt,
        promotedSiteIds: promoteOtherSites ? promotedSiteIds : [],
        language: {
          code: selectedLanguage.value,
          label: selectedLanguage.label,
          promptLabel: selectedLanguage.promptLabel,
          announcementLanguage: selectedLanguage.announcementLanguage,
          announcementVoice: selectedLanguage.announcementVoice,
        },
        voucherRequest: includeVoucher
          ? {
              enabled: true,
              offerDescription: String(voucherOfferDescription || '').trim(),
              terms: String(voucherTerms || '').trim() || DEFAULT_VOUCHER_TERMS,
            }
          : null,
      });
    }
    console.log('Sending prompt to server:', payload);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/creative/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      setLoading(false);
      setLoadingAction(null);
      if (!res.ok) {
        console.error('Failed to generate copy', res.status, await res.text());
        if (typeof onGenerateError === 'function') {
          onGenerateError({ stage: 'copy' });
        }
        return;
      }
      const data = await res.json();
      console.log('Received response from server:', data);
      if (typeof onGenerate === 'function') {
        onGenerate(data, { autoGenerateArtwork: Boolean(autoGenerateArtwork) });
      }
    } catch (err) {
      setLoading(false);
      setLoadingAction(null);
      console.error('Error fetching creative copy', err);
      if (typeof onGenerateError === 'function') {
        onGenerateError({ stage: 'copy', error: err });
      }
    }
  }, [
    includeVoucher,
    onGenerate,
    onGenerateError,
    onGenerateStart,
    promoteOtherSites,
    promotedSiteIds,
    requiresPromotedSiteSelection,
    requiresVoucherDescription,
    selectedLanguage,
    sharedProductsContext?.catalogId,
    siteId,
    voucherOfferDescription,
    voucherTerms,
  ]);

  const handleGenerate = async ({ autoGenerateArtwork }) => {
    await runGeneration({
      promptText: prompt,
      autoGenerateArtwork,
    });
  };

  useEffect(() => {
    if (!generationRequest || !requestKey || lastHandledRequestKeyRef.current === requestKey) return;
    const nextPrompt = String(generationRequest.prompt || '').trim();
    if (!nextPrompt) return;
    lastHandledRequestKeyRef.current = requestKey;
    setPrompt(nextPrompt);
    runGeneration({
      promptText: nextPrompt,
      autoGenerateArtwork: Boolean(generationRequest.autoGenerateArtwork),
    });
  }, [generationRequest, requestKey, runGeneration]);

  const handleNewCampaign = async () => {
    if (typeof window === 'undefined') return;
    const name = window.prompt('Enter a campaign name:');
    if (!name || !name.trim()) return;

    setLoading(true);
    setLoadingAction('new_campaign');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/creative/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: name.trim(), siteId }),
      });
      setLoading(false);
      setLoadingAction(null);
      if (!res.ok) {
        console.error('Failed to create campaign', res.status, await res.text());
        alert('Failed to create campaign. Please try again.');
        return;
      }
      const data = await res.json();
      if (typeof onGenerate === 'function') {
        onGenerate(data, { autoGenerateArtwork: false });
      }
    } catch (err) {
      setLoading(false);
      setLoadingAction(null);
      console.error('Error creating campaign', err);
      alert('Failed to create campaign. Please try again.');
    }
  };

  const hasPrompt = Boolean(prompt.trim());

  return (
    <div className="p-4 bg-gray-800 rounded-lg shadow">
      {showHeader ? (
        <>
          <h2 className="text-xl font-semibold mb-2 text-white">Campaign Creator</h2>
          <p className="mb-3 text-sm text-gray-300">
            Create the campaign copy that feeds TV, social, overlays, announcements, and landing pages.
          </p>
        </>
      ) : null}
      <div className="mb-4 space-y-3">
        <div className="rounded border border-gray-700 bg-gray-900/70 px-3 py-3 text-sm text-gray-200">
          <div>
            <span className="text-gray-400">This campaign belongs to:</span>{' '}
            <span className="font-semibold text-white">{site?.name || 'Current site'}</span>
          </div>
          {promoteOtherSites && selectedPromotedSites.length > 0 ? (
            <div className="mt-1">
              <span className="text-gray-400">This campaign promotes:</span>{' '}
              <span className="text-white">{selectedPromotedSites.map((siteOption) => buildSiteOptionLabel(siteOption)).join(', ')}</span>
            </div>
          ) : (
            <div className="mt-1 text-gray-400">This campaign promotes the host site itself.</div>
          )}
          {sharedProductsContext?.sourceCatalog && (
            <div className="mt-1">
              <span className="text-gray-400">Shared products source:</span>{' '}
              <span className="text-white">{buildSourceCatalogLabel(sharedProductsContext.sourceCatalog)}</span>
            </div>
          )}
        </div>

        <div className="rounded border border-gray-700 bg-gray-900/60 px-3 py-3">
          <label className="flex items-start gap-3 text-sm text-gray-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={promoteOtherSites}
              onChange={(e) => {
                setPromoteOtherSites(e.target.checked);
                if (!e.target.checked) {
                  setPromotedSiteIds([]);
                }
              }}
            />
            <span>
              <span className="block font-semibold text-white">Promote another site</span>
              <span className="mt-1 block text-xs text-gray-400">
                Write this campaign from the host site&apos;s point of view while encouraging a visit to one or more other venues in the same account.
              </span>
            </span>
          </label>

          {promoteOtherSites && (
            <div className="mt-4 space-y-3">
              {sitesLoading && <div className="text-sm text-gray-400">Loading available sites…</div>}
              {sitesError && <div className="text-sm text-red-300">{sitesError}</div>}
              {!sitesLoading && !sitesError && promotedSiteOptions.length === 0 && (
                <div className="text-sm text-gray-400">No other accessible sites are available for promotion.</div>
              )}
              {!sitesLoading && !sitesError && promotedSiteOptions.length > 0 && (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {promotedSiteOptions.map((siteOption) => {
                    const checked = promotedSiteIds.some((promotedSiteId) => String(promotedSiteId) === String(siteOption.id));
                    return (
                      <label
                        key={`promoted-site-${siteOption.id}`}
                        className={`flex items-start gap-3 rounded border px-3 py-3 ${
                          checked
                            ? 'border-indigo-500 bg-indigo-950/30'
                            : 'border-gray-700 bg-black/10 hover:bg-gray-900/60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={checked}
                          onChange={() => togglePromotedSite(siteOption.id)}
                        />
                        <span className="min-w-0">
                          <span className="block font-semibold text-white">{buildSiteOptionLabel(siteOption)}</span>
                          <span className="mt-1 block text-xs text-gray-400">
                            {String(siteOption?.business_type || siteOption?.primary_type_display_name || siteOption?.site_type || '').trim() || 'Venue'}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="text-xs text-gray-500">
                Promoted-site context changes the copy brief only. The campaign still belongs to the currently selected host site.
              </div>
              {requiresPromotedSiteSelection && (
                <div className="text-sm text-amber-300">
                  Select at least one promoted site to generate a multi-site campaign.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mb-4 rounded border border-gray-700 bg-gray-900/60 px-3 py-3">
        <label className="flex items-start gap-3 text-sm text-gray-200">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeVoucher}
            onChange={(e) => {
              setIncludeVoucher(e.target.checked);
              if (e.target.checked && !String(voucherTerms || '').trim()) {
                setVoucherTerms(DEFAULT_VOUCHER_TERMS);
              }
            }}
          />
          <span>
            <span className="block font-semibold text-white">Include a voucher</span>
            <span className="mt-1 block text-xs text-gray-400">
              Add the basic offer details here. The campaign generator will turn them into a ready-to-use landing page CTA, email subject, and voucher copy.
            </span>
          </span>
        </label>

        {includeVoucher && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-white">Offer details</label>
              <textarea
                className="w-full rounded bg-gray-950 px-3 py-2 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="e.g. 2-for-1 mini golf on your next visit, or a free cocktail with any main meal"
                value={voucherOfferDescription}
                onChange={(e) => setVoucherOfferDescription(e.target.value)}
              />
              <div className="mt-1 text-xs text-gray-500">
                Keep this factual. Renee will write the persuasive CTA and email-delivery copy around it.
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-white">Terms and conditions</label>
                <button
                  type="button"
                  className="text-xs font-medium text-indigo-300 hover:text-indigo-200"
                  onClick={() => setVoucherTerms(DEFAULT_VOUCHER_TERMS)}
                >
                  Reset common terms
                </button>
              </div>
              <textarea
                className="w-full rounded bg-gray-950 px-3 py-2 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={5}
                value={voucherTerms}
                onChange={(e) => setVoucherTerms(e.target.value)}
              />
              <div className="mt-1 text-xs text-gray-500">
                Customers open the voucher on their phone from the landing page, then save it, screenshot it, or share it before showing it at the venue.
              </div>
            </div>

            {requiresVoucherDescription && (
              <div className="text-sm text-amber-300">
                Add a short offer description to generate voucher content.
              </div>
            )}
          </div>
        )}
      </div>
      <textarea
        className="w-full p-2 rounded bg-gray-900 text-white"
        placeholder="e.g. Happy hour every Friday 5–7pm, 2‑for‑1 drinks"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
      />
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-200">
          <span>Language</span>
          <select
            className="bg-gray-900 text-white rounded px-2 py-1 border border-gray-700"
            value={language}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded text-white"
          onClick={() => handleGenerate({ autoGenerateArtwork: true })}
          disabled={!hasPrompt || loading || requiresPromotedSiteSelection || requiresVoucherDescription}
        >
          {loadingAction === 'copy_artwork' ? 'Generating…' : 'Generate Copy and Artwork'}
        </button>
        <button
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-white"
          onClick={() => handleGenerate({ autoGenerateArtwork: false })}
          disabled={!hasPrompt || loading || requiresPromotedSiteSelection || requiresVoucherDescription}
        >
          {loadingAction === 'copy_only' ? 'Generating…' : 'Generate Copy Only'}
        </button>
        <button
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-white"
          onClick={handleNewCampaign}
          disabled={loading}
        >
          {loadingAction === 'new_campaign' ? 'Creating…' : 'New Campaign'}
        </button>
      </div>
    </div>
  );
}
